import { useEffect, useRef } from 'react'
import { useDiagramStore, useHistoryStore, useSelectionStore, useViewportStore } from '@/stores'
import { useTableEditorModal } from '@/hooks/useTableEditorModal'
import { useModalStore } from '@/hooks/useModalStore'
import { useClipboardStore } from '@/stores/clipboardStore'
import { useConfirmStore } from '@/hooks/useConfirmStore'
import { useTabsStore } from '@/stores/tabsStore'
import { useTabViewStore } from '@/stores/tabViewStore'
import { useDraftStore } from '@/stores/draftStore'
import type { DiagramSnapshot } from '@/stores/historyStore'

// ── Helpers ───────────────────────────────────────────────────────────────────

/** Returns true when the focused element is a text-input-like target. */
function isTypingTarget(el: EventTarget | null): boolean {
  if (!el || !(el instanceof HTMLElement)) return false
  const tag = el.tagName.toLowerCase()
  if (['input', 'textarea', 'select'].includes(tag)) return true
  if (el.isContentEditable) return true
  return false
}

/** Returns true when any modal/panel/popup is currently open. */
function isAnyModalOpen(): boolean {
  const m = useModalStore.getState()
  const te = useTableEditorModal.getState()
  return (
    te.open ||
    m.defaultsOpen ||
    m.groupsOpen ||
    m.exportOpen ||
    m.versionsOpen ||
    m.shortcutsOpen ||
    m.aboutOpen ||
    m.createCommitOpen ||
    m.relationPopup.open ||
    m.fkPanel.open ||
    m.indexPanel.open ||
    m.sqlImport.open
  )
}

/** Read the canvas rect from the DOM. Returns null if element not found. */
function getCanvasRect(): DOMRect | null {
  return document.getElementById('cwrap')?.getBoundingClientRect() ?? null
}

function applyHistorySnapshot(snapshot: DiagramSnapshot) {
  useDiagramStore.setState({
    tables: snapshot.tables,
    rels: snapshot.rels,
    groups: snapshot.groups,
    projectName: snapshot.projectName,
    dialect: snapshot.dialect ?? 'postgresql',
  })
  useTabViewStore.getState().setHiddenTableIds(snapshot.hiddenTableIds ?? [])
}

// ── Hook ──────────────────────────────────────────────────────────────────────

/**
 * Registers global keyboard shortcuts for the MorphERD application.
 *
 * Safety rules:
 * - Shortcuts do NOT fire when the user is typing in an input/textarea/select
 *   or contenteditable element.
 * - Destructive shortcuts (Delete, Backspace, T, R, M, S) do NOT fire when any
 *   modal or panel is open.
 * - Ctrl+Z / Ctrl+Y fire as long as the user is not inside a text field,
 *   matching the behaviour of most desktop applications.
 * - Escape always fires (modal/dialog/ContextMenu components handle it
 *   internally with stopPropagation where needed).
 * - preventDefault is only called when the shortcut is actually handled.
 *
 * Register once at the AppShell level. The hook installs/removes event
 * listeners via useEffect — there is no duplicate listener risk.
 */
export function useKeyboardShortcuts(): void {
  // Track last mouse position to convert to canvas coordinates for T / Ctrl+V
  const lastMouseRef = useRef<{ clientX: number; clientY: number } | null>(null)

  // Mouse-move tracker
  useEffect(() => {
    const onMouseMove = (e: MouseEvent) => {
      lastMouseRef.current = { clientX: e.clientX, clientY: e.clientY }
    }
    document.addEventListener('mousemove', onMouseMove)
    return () => document.removeEventListener('mousemove', onMouseMove)
  }, [])

  // Main keydown handler
  useEffect(() => {
    const onKeyDown = (e: KeyboardEvent) => {
      const ctrl = e.ctrlKey || e.metaKey
      const { key } = e
      const lowerKey = key.toLowerCase()
      const readOnly = useDraftStore.getState().mode === 'view'

      // ── Escape — handled first, always ──────────────────────────────────────
      if (key === 'Escape') {
        const confirm = useConfirmStore.getState()
        if (confirm.open) {
          confirm.hide()
          return
        }
        // Modals and ContextMenu handle Escape themselves; we handle canvas state.
        const sel = useSelectionStore.getState()
        if (sel.relMode) {
          sel.cancelRelationshipCreation()
          return
        }
        if (sel.selectedTableId || sel.selectedTableIds.size > 0 || sel.selectedRelationshipId) {
          sel.clearSelection()
        }
        return
      }

      // ── Guard: no shortcuts while typing ────────────────────────────────────
      if (isTypingTarget(e.target)) return

      // ── Ctrl+Z — Undo ────────────────────────────────────────────────────────
      if (ctrl && lowerKey === 'z' && !e.shiftKey) {
        if (readOnly) return
        if (useTableEditorModal.getState().open) return
        e.preventDefault()
        const snap = useHistoryStore.getState().undo()
        if (snap) applyHistorySnapshot(snap)
        return
      }

      // ── Ctrl+Y and Ctrl+Shift+Z — Redo ──────────────────────────────────────
      if (ctrl && (lowerKey === 'y' || (e.shiftKey && lowerKey === 'z'))) {
        if (readOnly) return
        if (useTableEditorModal.getState().open) return
        e.preventDefault()
        const snap = useHistoryStore.getState().redo()
        if (snap) applyHistorySnapshot(snap)
        return
      }

      if (ctrl && lowerKey === 'i') {
        if (readOnly) return
        if (isAnyModalOpen()) return
        e.preventDefault()
        useModalStore.getState().openSqlImport('editor-import')
        return
      }

      if (ctrl && lowerKey === 'e') {
        if (isAnyModalOpen()) return
        if (!useTabsStore.getState().activeTabId) return
        e.preventDefault()
        useModalStore.getState().openExport()
        return
      }

      // ── Ctrl+S — disabled; keep preventing browser "save page" dialog. ──────
      if (ctrl && lowerKey === 's') {
        e.preventDefault()
        return
      }

      // ── Ctrl+C — Copy selected table(s) ─────────────────────────────────────
      if (ctrl && lowerKey === 'a') {
        if (isAnyModalOpen()) return
        const tableIds = useDiagramStore.getState().tables.map((table) => table.id)
        if (tableIds.length === 0) return
        e.preventDefault()
        useSelectionStore.getState().selectMultipleTables(tableIds)
        return
      }

      // ── Ctrl+C — Copy selected table(s) ─────────────────────────────────────
      if (ctrl && lowerKey === 'c') {
        if (isAnyModalOpen()) return
        const { selectedTableId, selectedTableIds } = useSelectionStore.getState()
        const ids = selectedTableIds.size > 0
          ? [...selectedTableIds]
          : selectedTableId
          ? [selectedTableId]
          : []
        if (ids.length > 0) {
          e.preventDefault()
          useClipboardStore.getState().copyTables(ids)
        }
        return
      }

      // ── Ctrl+V — Paste table(s) ──────────────────────────────────────────────
      if (ctrl && lowerKey === 'v') {
        if (readOnly) return
        if (isAnyModalOpen()) return
        const { copiedTables } = useClipboardStore.getState()
        if (copiedTables.length === 0) return
        e.preventDefault()
        const mouse = lastMouseRef.current
        let pos: { x: number; y: number } | undefined
        if (mouse) {
          const rect = getCanvasRect()
          if (rect) {
            pos = useViewportStore.getState().clientToCanvas(mouse.clientX, mouse.clientY, rect)
          }
        }
        useClipboardStore.getState().pasteTables(pos)
        return
      }

      // ── Ctrl+D — Duplicate selected table(s) ────────────────────────────────
      if (ctrl && lowerKey === 'd') {
        if (readOnly) return
        e.preventDefault()
        if (isAnyModalOpen()) return
        const { selectedTableId, selectedTableIds } = useSelectionStore.getState()
        const { duplicateTable } = useDiagramStore.getState()
        const ids = selectedTableIds.size > 1
          ? [...selectedTableIds]
          : selectedTableId
          ? [selectedTableId]
          : []
        for (const id of ids) {
          duplicateTable(id)
        }
        return
      }

      // ── From here on: single-key shortcuts — blocked when modal is open ──────
      if (isAnyModalOpen()) return

      // ── T — Create table at mouse position ───────────────────────────────────
      if (key === 't' || key === 'T') {
        if (readOnly) return
        e.preventDefault()
        const mouse = lastMouseRef.current
        let pos: { x: number; y: number } | undefined
        if (mouse) {
          const rect = getCanvasRect()
          if (rect) {
            pos = useViewportStore.getState().clientToCanvas(mouse.clientX, mouse.clientY, rect)
          }
        }
        useTableEditorModal.getState().openCreate({ position: pos ?? null })
        return
      }

      // ── R — Start relationship creation ──────────────────────────────────────
      if (key === 'r' || key === 'R') {
        if (readOnly) return
        e.preventDefault()
        const sel = useSelectionStore.getState()
        sel.startRelationshipCreation(sel.selectedTableId ?? undefined, sel.relType)
        return
      }

      // ── M — Pan mode ─────────────────────────────────────────────────────────
      if (key === 'm' || key === 'M') {
        e.preventDefault()
        useSelectionStore.getState().setCanvasMode('pan')
        return
      }

      // ── S — Select mode ──────────────────────────────────────────────────────
      // Note: Ctrl+S is handled above and never reaches here.
      if ((key === 's' || key === 'S') && !ctrl) {
        e.preventDefault()
        useSelectionStore.getState().setCanvasMode('select')
        return
      }

      // ── Delete / Backspace — Delete selected table or relationship ────────────
      if (key === 'Delete' || key === 'Backspace') {
        if (readOnly) return
        const { selectedRelationshipId, selectedTableId, selectedTableIds } =
          useSelectionStore.getState()

        if (selectedRelationshipId) {
          e.preventDefault()
          useDiagramStore.getState().deleteRelationship(selectedRelationshipId)
          useSelectionStore.getState().clearSelection()
          return
        }

        const ids =
          selectedTableIds.size > 0
            ? [...selectedTableIds]
            : selectedTableId
            ? [selectedTableId]
            : []

        if (ids.length === 0) return
        e.preventDefault()

        const tableNames = ids
          .map((id) => {
            const t = useDiagramStore.getState().tables.find((tbl) => tbl.id === id)
            return t?.name ?? id
          })
          .join(', ')

        useConfirmStore.getState().show(
          ids.length === 1
            ? 'Deletar tabela'
            : `Deletar ${ids.length} tabelas`,
          `Tem certeza que deseja deletar "${tableNames}"? Todos os relacionamentos associados também serão removidos.`,
          () => {
            const { deleteTable, deleteTables } = useDiagramStore.getState()
            if (ids.length === 1) {
              deleteTable(ids[0]!)
            } else {
              deleteTables(ids)
            }
            useSelectionStore.getState().clearSelection()
          },
        )
        return
      }
    }

    document.addEventListener('keydown', onKeyDown)
    return () => document.removeEventListener('keydown', onKeyDown)
  }, [])
}
